import React, { FC, useRef, useState, useEffect, ReactNode } from 'react';
import { Tooltip, Typography, TypographyProps } from '@mui/material';
import { mergeSx } from '@client/helpers/mui.helpers';
import { useEllipsisStyles } from './ellipsis.styles';

const inheritTypographySx = {
  fontSize: 'inherit',
  color: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
} as const;

interface EllipsisTextProps extends Omit<TypographyProps, 'align'> {
  children?: ReactNode;
  showTooltip?: boolean;
  tooltipTitle?: string;
  /* vertical alignment of text. If EllipsisText screws up text alignment, try setting this to 'middle' */
  verticalAlign?: 'top' | 'middle' | 'bottom' | 'baseline';
}

/**
 * This component is used to display a text with an ellipsis and a tooltip.
 *
 * It should never display tooltip if the text is not truncated.
 *
 * @param children - Will be used as tooltip if tooltipTitle is not provided. Should in that case be simple strings that can be displayed in a single line.
 * @param showTooltip - Whether to show the tooltip.
 * @param tooltipTitle - The title to be displayed in the tooltip.
 * @param typographyProps - Props to be passed to the Typography component.
 * @returns A component that displays a text with an ellipsis and a tooltip.
 */
export const EllipsisText: FC<EllipsisTextProps> = ({
  children,
  showTooltip = true,
  tooltipTitle,
  verticalAlign,
  className,
  ...typographyProps
}) => {
  const tooltip = tooltipTitle ?? children;
  // text truncation is only used to control if we should show the tooltip or not
  const { ref, isTruncated } = useTextTruncation(
    showTooltip ? 'enabled' : 'disabled', // only run the check if we want to show the tooltip
    tooltip?.toString(),
  );
  const { classes, cx } = useEllipsisStyles({ verticalAlign: verticalAlign });

  return (
    <Tooltip
      title={isTruncated && showTooltip ? tooltip : undefined}
      disableHoverListener={!showTooltip && !isTruncated}
      disableInteractive
    >
      <Typography
        ref={ref}
        component="span"
        className={cx(classes.ellipsis, className)}
        {...typographyProps}
        sx={mergeSx(inheritTypographySx, typographyProps.sx)}
      >
        {children}
      </Typography>
    </Tooltip>
  );
};

// claude wrote this
const useTextTruncation = (state: 'enabled' | 'disabled', tooltip?: string) => {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (state === 'disabled') {
      setIsTruncated(false);
      return;
    }

    const checkTruncation = () => {
      const element = ref.current;
      if (!element) return;

      // Create a temporary element to measure natural text width
      const temp = document.createElement('span');
      temp.style.visibility = 'hidden';
      temp.style.position = 'absolute';
      temp.style.whiteSpace = 'nowrap';
      temp.style.font = window.getComputedStyle(element).font;
      temp.textContent = element.textContent;

      document.body.appendChild(temp);
      const naturalWidth = temp.offsetWidth;
      document.body.removeChild(temp);

      setIsTruncated(naturalWidth > element.clientWidth);
    };

    const initialCheckTimeout = setTimeout(checkTruncation, 50); // not sure if needed, but I had some issues with this not working.. might have been solved by other code.
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(initialCheckTimeout);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [state, tooltip]);

  return { ref, isTruncated };
};
